import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'

import { isTransientSourceError, SOURCE_ISSUE_STYLE } from '@shared/components/ciec-analysis-view/snapshot-labels.constants'
import { SnapshotResponse, SnapshotSection, SnapshotView } from '@data/interfaces/verification-snapshot.interface'

/**
 * Los 12 estados de UI de la foto de Syntage (F-158). Los de sobre
 * (`pre-snapshot`, `no-verification`, `forbidden`, `load-error`) sustituyen la pestaña entera; los de
 * sección se pintan en el lugar de esa sección; `reconstructed` y `pinned` son avisos que acompañan
 * al contenido, no lo reemplazan.
 */
export type SnapshotUiState =
  'loading' | 'empty' | 'load-error' | 'pending' | 'unavailable' | 'not-requested' | 'source-error' | 'reconstructed' | 'pre-snapshot' | 'no-verification' | 'forbidden' | 'pinned'

/**
 * El estado que pinta una sección, o `null` si tiene datos que mostrar.
 *
 * ! `complete` + `empty` es «Syntage consultó y no encontró nada», que NO es lo mismo que «no se
 * ! consultó» (`not_requested`). Confundirlos es justo el «Limpio» falso que F-158 corrige.
 * Una sección que el backend ni siquiera mandó se trata como no disponible, nunca como vacía.
 */
export function sectionUiState(section: SnapshotSection | null | undefined): SnapshotUiState | null {
  if (!section) return 'unavailable'
  if (section.status === 'complete') return section.empty ? 'empty' : null
  if (section.status === 'pending') return 'pending'
  if (section.status === 'not_requested') return 'not-requested'
  if (section.status === 'error') return 'source-error'
  return 'unavailable'
}

/** El estado de la pestaña entera según el sobre, o `null` si hay foto que pintar. */
export function viewUiState(view: SnapshotView<string>): SnapshotUiState | null {
  if (view.state === 'pre_snapshot') return 'pre-snapshot'
  if (view.state === 'no_verification') return 'no-verification'
  return null
}

/**
 * El texto de cada estado de sección, el mismo que pinta el bloque. Lo usan también las líneas «Sin registros:» de
 * Buró y Legal, que lo ponen en el `title` y para el lector de pantalla de cada fuente.
 */
export const SECTION_STATE_TEXTS: Partial<Record<SnapshotUiState, string>> = {
  empty: 'Syntage consultó esta fuente y no encontró registros.',
  pending: 'Syntage aún está procesando esta fuente. Se completará sola en las próximas horas.',
  unavailable: 'Syntage no entregó esta fuente dentro de las 48 h siguientes al análisis.',
  'not-requested': 'Esta fuente no se pidió en Syntage para este cliente.',
  'source-error': 'Syntage respondió con error al consultar esta fuente.',
}

/** El texto completo de una sección sin datos, con el motivo cuando el cliente no está en Syntage. */
export function sectionStateText(state: SnapshotUiState, reason?: string | null): string {
  const text = SECTION_STATE_TEXTS[state] ?? ''
  return state === 'unavailable' && reason === 'NO_SYNTAGE_ENTITY' ? `${text} Motivo: el cliente no está registrado en Syntage.` : text
}

/** Un 403 es falta de permiso, no algo que se arregle reintentando; cualquier otro fallo, sí. */
export function loadErrorUiState(response: SnapshotResponse<unknown>): SnapshotUiState {
  return response.status === 403 ? 'forbidden' : 'load-error'
}

/** Bloque reutilizable que pinta un estado con su texto exacto. Solo `load-error` ofrece «Reintentar». */
@Component({
  selector: 'app-snapshot-state',
  templateUrl: './snapshot-state.component.html',
  styleUrls: ['./snapshot-state.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnapshotStateComponent {
  @Input() state: SnapshotUiState = 'loading'
  /** Cierre de la ventana de 48 h (`snapshot.windowEndsAt`), para `pending`. */
  @Input() closesAt?: string | null
  /** Motivo de máquina de la sección: `NO_SYNTAGE_ENTITY` en `unavailable`; `SYNTAGE_UNAVAILABLE`, `HTTP_503`… en `source-error`. */
  @Input() reason?: string | null
  /** Cuándo se armó la foto reconstruida, para `reconstructed`. */
  @Input() builtAt?: string | null
  /** Cuándo se hizo el análisis original, para `reconstructed`. */
  @Input() analyzedAt?: string | null
  /** Una línea compacta en vez de la caja: para un estado que va a todo el ancho de una tarjeta (reporte de Buró vacío). */
  @Input() compact = false
  /** Para `pre-snapshot` en Ratios: debajo van los ratios de Syntage que guardó el análisis, no «lo de Análisis». */
  @Input() savedRatios = false
  @Output() retry = new EventEmitter<void>()

  /** Ícono de lo pendiente y del error de la fuente: los mismos de Análisis y Legal. */
  readonly issue = SOURCE_ISSUE_STYLE
  readonly texts = SECTION_STATE_TEXTS

  /** Error de la fuente que el sistema reintenta solo unas vueltas del cron (no toda la ventana); los definitivos no llevan aviso. */
  get retriesAutomatically(): boolean {
    return isTransientSourceError(this.reason)
  }

  onRetry(): void {
    this.retry.emit()
  }
}
